import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { postCoupon } from "../../Redux/Reducers/CoupansReducer";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

/* -------------------- OPTIONS -------------------- */

const categoryOptions = [
  { value: "flat_extra_rupees", label: "Flat Rs. Extra" },
  { value: "flat_extra_percentage", label: "Percent Extra" },
];

const packMappingOptions = [
  { value: "full_category", label: "Full Category" },
  { value: "selective_packs", label: "Selective Packs" },
  { value: "min_value", label: "Minimum Value" },
];

const rechargeCategoryOptions = [
  { value: "First Recharge", label: "First Recharge" },
  { value: "Second Recharge", label: "Second Recharge" },
  { value: "Third Recharge", label: "Third Recharge" },
  { value: "Fourth Recharge", label: "Fourth Recharge" },
  { value: "Fifth Recharge", label: "Fifth Recharge" },
];

const platformsOptions = [
  { value: "web", label: "Web" },
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];

/* -------------------- COMPONENT -------------------- */

function AddConsultationCoupon() {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [giftAmountOptions, setGiftAmountOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [couponAvailability, setCouponAvailability] = useState(null); // null | 'checking' | 'available' | 'taken'

  // 🔥 NEW STATES
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Per-category state when toggle is ON
  const [allCategoryGiftOptions, setAllCategoryGiftOptions] = useState({});
  const [perCategoryState, setPerCategoryState] = useState(() =>
    rechargeCategoryOptions.reduce((acc, cat) => {
      acc[cat.value] = { giftAmountIds: [], selectedPackId: null, minActualAmount: "" };
      return acc;
    }, {})
  );

  const selectedPackMapping = watch("packMappingType");
  const selectedRechargeCategory = watch("rechargeCategory");
  const selectedDiscountCategory = watch("category");
  const validFromValue = watch("validFrom");
  const giftAmountIds = watch("giftAmountIds");
  const isAllRechargeCategories = watch("allRechargeCategories");

  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  /* -------------------- FETCH GIFT AMOUNTS (single category) -------------------- */

  useEffect(() => {
    if (isAllRechargeCategories) return; // handled separately
    const fetchGiftAmounts = async () => {
      if (!selectedRechargeCategory?.value) return;
      try {
        setIsLoading(true);
        const token = localStorage.getItem("astroVani-admin-token");
        const res = await axios.get(
          `${process.env.REACT_APP_BASEURL}/admin/giftAmount?category=${encodeURIComponent(
            selectedRechargeCategory.value
          )}`,
          { headers: { Authorization: token } }
        );
        const options =
          res?.data?.data?.map((item) => ({
            value: item._id,
            label: `${item.actualAmount} - Gift: ${item.giftAmount}`,
          })) || [];
        setGiftAmountOptions(options);
      } catch (err) {
        toast.error("Failed to load gift amounts");
      } finally {
        setIsLoading(false);
      }
    };
    fetchGiftAmounts();
  }, [selectedPackMapping, selectedRechargeCategory, isAllRechargeCategories, setValue]);

  /* -------------------- FETCH GIFT AMOUNTS (all categories) -------------------- */

  useEffect(() => {
    if (!isAllRechargeCategories) return;
    const fetchAll = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("astroVani-admin-token");
        const results = await Promise.all(
          rechargeCategoryOptions.map((cat) =>
            axios.get(
              `${process.env.REACT_APP_BASEURL}/admin/giftAmount?category=${encodeURIComponent(cat.value)}`,
              { headers: { Authorization: token } }
            )
          )
        );
        const newOptions = {};
        rechargeCategoryOptions.forEach((cat, idx) => {
          newOptions[cat.value] =
            results[idx]?.data?.data?.map((item) => ({
              value: item._id,
              label: `${item.actualAmount} - Gift: ${item.giftAmount}`,
            })) || [];
        });
        setAllCategoryGiftOptions(newOptions);
      } catch (err) {
        toast.error("Failed to load gift amounts for all categories");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, [isAllRechargeCategories]);

  /* -------------------- PER-CATEGORY STATE UPDATER -------------------- */

  const updatePerCategory = (categoryValue, field, value) => {
    setPerCategoryState((prev) => ({
      ...prev,
      [categoryValue]: {
        ...prev[categoryValue],
        [field]: value,
        // Reset selectedPackId if giftAmountIds change
        ...(field === "giftAmountIds" ? { selectedPackId: null } : {}),
      },
    }));
  };

  /* -------------------- COUPON CODE AVAILABILITY CHECK -------------------- */

  const checkCouponAvailability = async (code) => {
    const trimmed = code?.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

    if (!trimmed || trimmed.length < 4 || !/^[A-Z0-9]+$/.test(trimmed)) {
      setCouponAvailability(null);
      return;
    }

    try {
      setCouponAvailability("checking");

      const token = localStorage.getItem("astroVani-admin-token");

      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/coupon/check-availability`,
        { couponCode: trimmed },
        { headers: { Authorization: token } }
      );

      const isAvailable = res?.data?.data?.isAvailable;

      if (res?.data?.success && isAvailable === true) {
        setCouponAvailability("available");
      } else {
        setCouponAvailability("taken");
      }
    } catch (err) {
      if (err?.response?.status === 400 || err?.response?.status === 409) {
        setCouponAvailability("taken");
      } else {
        setCouponAvailability(null);
      }
    }
  };

  /* -------------------- SUBMIT -------------------- */

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      const payload = {
        couponName: data.couponName,
        description: data.description,
        validFrom: (data.validFrom),
        validTo: (data.validTo),
        category: data.category.value,
        discountValue: Number(data.discountValue),
        packMappingType: data.packMappingType.value,
        allRechargeCategories: !!data.allRechargeCategories,
      };

      if (data.couponCode?.trim()) {
        payload.couponCode = data.couponCode?.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
      }

      if (data.platforms?.length) {
        payload.platforms = data.platforms.map((p) => p.value);
      }

      if (data.allRechargeCategories) {
        // Build allCategoriesData from perCategoryState
        payload.allCategoriesData = rechargeCategoryOptions.map((cat) => {
          const catState = perCategoryState[cat.value] || {};
          const catData = {
            rechargeCategory: cat.value,
            giftAmountIds: (catState.giftAmountIds || []).map((g) => g.value),
            selectedPackId: catState.selectedPackId?.value || null,
          };
          if (data.packMappingType.value === "min_value") {
            catData.minActualAmount = Number(catState.minActualAmount || 0);
          }
          return catData;
        });
      } else {
        // Single category flow
        payload.rechargeCategory = data.rechargeCategory.value;

        if (data.packMappingType.value === "min_value") {
          payload.minActualAmount = Number(data.minActualAmount);
        }
        if (data.packMappingType.value === "selective_packs") {
          payload.giftAmountIds = data.giftAmountIds.map((g) => g.value);
        }
        if (data?.selectedPackId) {
          payload.selectedPackId = data.selectedPackId.value;
        }
      }

      const res = await dispatch(postCoupon(payload));

      // 🔥 HANDLE OVERLAP
      if (res?.payload?.data?.confirmOverlap) {
        setPendingPayload(payload);
        setOpenConfirm(true);
        setIsLoading(false);
        return;
      }

      if (res?.payload?.success) {
        toast.success(res.payload.message);
        navigate("/consultation-coupon");
      } else {
        toast.error(res?.error?.message || res?.payload?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Failed to create coupon");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- CONFIRM BUTTON CLICK -------------------- */
  const handleConfirm = async () => {
    setOpenConfirm(false);
    setIsLoading(true);

    try {
      const payload = {
        ...pendingPayload,
        confirmOverlap: true, // ✅ important
      };

      const res = await dispatch(postCoupon(payload));

      if (res?.payload?.success) {
        toast.success(res.payload.message);
        navigate("/consultation-coupon");
      } else {
        toast.error(res?.error?.message || res?.payload?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Failed to create coupon");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <>
      <Toaster position="top-right" />

      {/* 🔥 Confirm Dialog */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Overlap</DialogTitle>
        <DialogContent>
          <Typography>
            An active coupon already exists for the selected duration. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Create Coupon</h2>
          <button
            type="button"
            className="btn btn-primary"
            style={{
              padding: "7px 20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 500,
              borderRadius: "6px"
            }}
            onClick={() => navigate("/consultation-coupon")}
          >
            <i className="icofont-arrow-left fs-6"></i>
            Back
          </button>
        </div>

        <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
          {/* Coupon Name */}
          <div className="col-md-6">
            <label className="form-label">Coupon Name</label>
            <input
              className={`form-control ${errors.couponName ? "is-invalid" : ""}`}
              {...register("couponName", { required: "Coupon name is required" })}
            />
            {errors.couponName && (
              <small className="text-danger">{errors.couponName.message}</small>
            )}
          </div>

          {/* Coupon Code */}
          <div className="col-md-6">
            <label className="form-label">Coupon Code (Optional)</label>
            <input
              className={`form-control ${errors.couponCode
                ? "is-invalid"
                : couponAvailability === "available"
                  ? "is-valid"
                  : couponAvailability === "taken"
                    ? "is-invalid"
                    : ""
                }`}
              {...register("couponCode", {
                onChange: (e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  setValue("couponCode", val, { shouldValidate: true });
                  setCouponAvailability(null);
                },
                validate: (val) => {
                  if (!val || val.trim() === "") return true;
                  const trimmed = val.trim();
                  if (trimmed.length < 4) return "Minimum 4 characters required";
                  if (trimmed.length > 12) return "Maximum 12 characters allowed";
                  if (!/^[a-zA-Z0-9]+$/.test(trimmed))
                    return "Only alphanumeric characters (A-Z, 0-9) are allowed";
                  if (couponAvailability === "taken")
                    return "This coupon code is already taken";
                  return true;
                },
              })}
              onKeyDown={(e) => {
                if (
                  e.ctrlKey ||
                  e.metaKey ||
                  e.altKey ||
                  [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                    "Tab",
                    "Home",
                    "End",
                    "Enter",
                  ].includes(e.key)
                ) {
                  return;
                }
                if (!/^[a-zA-Z0-9]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder="e.g. FESTIVE50"
              maxLength={12}
              onBlur={(e) => checkCouponAvailability(e.target.value)}
            />

            {errors.couponCode ? (
              <small className="text-danger">{errors.couponCode.message}</small>
            ) : couponAvailability === "checking" ? (
              <small className="text-muted">⏳ Checking availability...</small>
            ) : couponAvailability === "available" ? (
              <small className="text-success">✅ Coupon code is available!</small>
            ) : couponAvailability === "taken" ? (
              <small className="text-danger">❌ This coupon code is already taken.</small>
            ) : (
              <small className="text-muted">
                Min 4, Max 12 chars. Only alphanumeric characters allowed.
              </small>
            )}
          </div>

          {/* Platforms */}
          <div className="col-md-6">
            <label className="form-label">Platforms</label>
            <Controller
              name="platforms"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={platformsOptions}
                  isMulti
                  placeholder="Select platforms"
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              )}
            />
            {errors.platforms && (
              <small className="text-danger">{errors.platforms.message}</small>
            )}
          </div>

          {/* Description */}
          <div className="col-md-6">
            <label className="form-label">Description</label>
            <input
              className={`form-control ${errors.description ? "is-invalid" : ""}`}
              {...register("description", { required: "Description is required" })}
            />
            {errors.description && (
              <small className="text-danger">{errors.description.message}</small>
            )}
          </div>

          {/* Discount Category */}
          <div className="col-md-6">
            <label className="form-label">Discount Category</label>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Select discount category" }}
              render={({ field }) => (
                <>
                  <Select {...field} options={categoryOptions} />
                  {errors.category && (
                    <small className="text-danger">{errors.category.message}</small>
                  )}
                </>
              )}
            />
          </div>

          {/* Discount Value */}
          <div className="col-md-6">
            <label className="form-label">
              {selectedDiscountCategory?.value?.includes("percentage")
                ? "Extra Discount %"
                : "Extra Amount"}
            </label>
            <input
              type="number"
              className={`form-control ${errors.discountValue ? "is-invalid" : ""}`}
              {...register("discountValue", {
                required: "Discount value is required",
                min: { value: 1, message: "Value must be at least 1" },
              })}
            />
            {errors.discountValue && (
              <small className="text-danger">{errors.discountValue.message}</small>
            )}
          </div>

          {/* Valid Dates */}
          <div className="col-md-6">
            <label className="form-label">Valid From</label>
            <input
              type="date"
              className="form-control"
              min={todayDate}
              {...register("validFrom", {
                required: "Valid From date is required",
                validate: (value) => {
                  const selectedDate = new Date(value);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (selectedDate < today) return "Valid From date cannot be before today";
                  return true;
                },
              })}
            />
            {errors.validFrom && (
              <span className="text-danger" style={{ fontSize: "12px" }}>
                {errors.validFrom.message}
              </span>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Valid To</label>
            <input
              type="date"
              className="form-control"
              min={validFromValue || todayDate}
              {...register("validTo", {
                required: "Valid To date is required",
                validate: (value) => {
                  if (!validFromValue) return "Please select Valid From date first";
                  const validToDate = new Date(value);
                  const validFromDate = new Date(validFromValue);
                  if (validToDate < validFromDate) return "Valid To date cannot be before Valid From date";
                  return true;
                },
              })}
            />
            {errors.validTo && (
              <span className="text-danger" style={{ fontSize: "12px" }}>
                {errors.validTo.message}
              </span>
            )}
          </div>

          {/* Pack Mapping */}
          <div className="col-md-6">
            <label className="form-label">Pack Mapping Type</label>
            <Controller
              name="packMappingType"
              control={control}
              rules={{ required: "Pack mapping type is required" }}
              render={({ field }) => (
                <>
                  <Select {...field} options={packMappingOptions} />
                  {errors.packMappingType && (
                    <small className="text-danger">{errors.packMappingType.message}</small>
                  )}
                </>
              )}
            />
          </div>

          {/* All Recharge Categories Toggle */}
          <div className="col-md-12 mt-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allRechargeCategories"
                {...register("allRechargeCategories")}
                style={{ width: "40px", height: "20px", cursor: "pointer" }}
              />
              <label
                className="form-check-label ms-2 fw-bold"
                htmlFor="allRechargeCategories"
                style={{ cursor: "pointer", paddingTop: "2px" }}
              >
                Do you want to create this coupon for all recharge categories?
              </label>
            </div>
          </div>

          {/* ===== TOGGLE OFF: Single Category Flow ===== */}
          {!isAllRechargeCategories && (
            <>
              {/* Recharge Category */}
              <div className="col-md-6">
                <label className="form-label">Recharge Category</label>
                <Controller
                  name="rechargeCategory"
                  control={control}
                  rules={{ required: "Recharge category is required" }}
                  render={({ field }) => (
                    <>
                      <Select {...field} options={rechargeCategoryOptions} />
                      {errors.rechargeCategory && (
                        <small className="text-danger">{errors.rechargeCategory.message}</small>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Minimum Value */}
              {selectedPackMapping?.value === "min_value" && (
                <div className="col-md-6">
                  <label className="form-label">
                    Minimum Recharge Value
                    <small className="text-muted">
                      {" "}(Coupon will be applicable to recharge packs having amount equal or greater than this value)
                    </small>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${errors.minActualAmount ? "is-invalid" : ""}`}
                    {...register("minActualAmount", {
                      required: "Minimum value is required",
                      min: { value: 1, message: "Value must be at least 1" },
                    })}
                  />
                  {errors.minActualAmount && (
                    <small className="text-danger">{errors.minActualAmount.message}</small>
                  )}
                </div>
              )}

              {/* Gift Amounts */}
              {selectedPackMapping?.value === "selective_packs" && (
                <div className="col-md-6">
                  <label className="form-label">Gift Amounts</label>
                  <Controller
                    name="giftAmountIds"
                    control={control}
                    rules={{ required: "Select at least one gift amount" }}
                    render={({ field }) => (
                      <>
                        <Select
                          {...field}
                          isMulti
                          options={giftAmountOptions}
                          isLoading={isLoading}
                          onChange={(selectedOptions) => {
                            field.onChange(selectedOptions);
                            setValue("selectedPackId", null);
                          }}
                        />
                        {errors.giftAmountIds && (
                          <small className="text-danger">{errors.giftAmountIds.message}</small>
                        )}
                      </>
                    )}
                  />
                </div>
              )}

              {/* Select a pack to show as pre-filled */}
              <div className="col-md-6">
                <label className="form-label">Select a pack to show as pre-filled</label>
                <Controller
                  name="selectedPackId"
                  control={control}
                  rules={{ required: "Select at least one gift amount" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={
                        selectedPackMapping?.value === "selective_packs"
                          ? giftAmountIds
                          : giftAmountOptions
                      }
                      isLoading={isLoading}
                    />
                  )}
                />
              </div>
            </>
          )}

          {/* ===== TOGGLE ON: All Categories Flow ===== */}
          {isAllRechargeCategories && (
            <div className="col-12">
              {rechargeCategoryOptions.map((cat) => {
                const catOptions = allCategoryGiftOptions[cat.value] || [];
                const catState = perCategoryState[cat.value] || {};
                const packOptions =
                  selectedPackMapping?.value === "selective_packs"
                    ? catState.giftAmountIds || []
                    : catOptions;

                return (
                  <div
                    key={cat.value}
                    className="card mb-3 border"
                    style={{ borderRadius: "10px" }}
                  >
                    <div
                      className="card-header fw-bold"
                      style={{
                        backgroundColor: "#f1f3f5",
                        borderRadius: "10px 10px 0 0",
                        fontSize: "15px",
                      }}
                    >
                      {cat.label}
                    </div>
                    <div className="card-body row g-3">
                      {/* Minimum Recharge Value */}
                      {selectedPackMapping?.value === "min_value" && (
                        <div className="col-md-6">
                          <label className="form-label">
                            Minimum Recharge Value
                            <small className="text-muted">
                              {" "}(Equal or greater)
                            </small>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            min={1}
                            value={catState.minActualAmount || ""}
                            onChange={(e) =>
                              updatePerCategory(cat.value, "minActualAmount", e.target.value)
                            }
                          />
                        </div>
                      )}

                      {/* Gift Amounts */}
                      {selectedPackMapping?.value === "selective_packs" && (
                        <div className="col-md-6">
                          <label className="form-label">Gift Amounts</label>
                          <Select
                            isMulti
                            options={catOptions}
                            isLoading={isLoading}
                            value={catState.giftAmountIds || []}
                            onChange={(val) =>
                              updatePerCategory(cat.value, "giftAmountIds", val)
                            }
                          />
                        </div>
                      )}

                      {/* Select a pack to show as pre-filled */}
                      <div className="col-md-6">
                        <label className="form-label">Select a pack to show as pre-filled</label>
                        <Select
                          options={packOptions}
                          isLoading={isLoading}
                          value={catState.selectedPackId || null}
                          onChange={(val) =>
                            updatePerCategory(cat.value, "selectedPackId", val)
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Submit */}
          <div className="col-12">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create Coupon"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AddConsultationCoupon;
